import React, { useEffect, useRef } from "react";
import { Chart, registerables } from "chart.js";
import { Link } from "react-router-dom";
import { UserActivityPanel } from "../analytics/UserActivityPanel";
import { useLanguage } from "../../i18n/LanguageProvider";
import { countryFlag, countryName, seRegionName } from "../../utils/geo-labels";
import { formatDateTime, formatRelative } from "./dates";

Chart.register(...registerables);

interface TopRow {
  target: string;
  hits: number;
  // Origin of the event (page views are split into "app" vs "marketing").
  // Optional: other top-lists (areas, CTAs…) don't carry it.
  source?: string;
}

interface NewUser {
  id: string;
  email: string;
  username: string | null;
  email_verified: boolean;
  plan_name: string | null;
  created_at: string;
  last_login_at: string | null;
  last_seen_at: string | null;
  country: string | null;
  referrer_host: string | null;
  utm_source: string | null;
}

interface UserStats {
  totals: { total: number; last7: number; prev7: number; in_window: number; active7: number };
  newLastWeek: NewUser[];
  signupsPerDay: Array<{ day: string; signups: number }>;
}

interface InsightsTabProps {
  telemetry:
    | {
        dau: Array<{ day: string; users: number }>;
        anonVisits: Array<{ day: string; sessions: number }>;
        uniqueIps?: Array<{ day: string; visitors: number }>;
        uniqueIpsTotal?: { total: number; authed: number; anon: number };
        topRoutes: TopRow[];
        topAreas: TopRow[];
        ctaClicks: TopRow[];
        modalOpens: TopRow[];
        topCountries?: TopRow[];
        topRegions?: TopRow[];
        topSources?: TopRow[];
        forecastRange?: TopRow[];
        featureUse?: TopRow[];
        forum?: {
          totals: { views: number; threads: number; posts: number; posters: number; named_members: number };
          topThreads: Array<{ id: string; title: string; kind: string; views: number; replies: number }>;
          pageViews: TopRow[];
          postsPerDay: Array<{ day: string; posts: number }>;
        };
        users?: UserStats;
        freeTierAtLimit: Array<{ account_id: string; email: string; active_days: number }>;
      }
    | null;
  telemetryLoading: boolean;
  canSeeUserInsights: boolean;
  days: number;
  onDaysChange: (days: number) => void;
}

// Readable names for feature_use / modal_open targets.
const FEATURE_LABELS: Record<string, string> = {
  grid_mix: "Produktionsmix (stor vy)",
  grid_mix_range_36h: "Produktionsmix: 36 h",
  grid_mix_range_168h: "Produktionsmix: 7 dygn",
  historical_prices: "Historiska priser",
  forum_thread: "Forum: ny tråd",
  forum_reply: "Forum: svar",
  forum_reply_daily: "Forum: svar i dagens prognos",
  forum_vote: "Forum: röst",
  forum_quote: "Forum: citat",
  forum_share: "Forum: dela länk",
};
const featureLabel = (t: string) => FEATURE_LABELS[t] ?? t;
const SOURCE_LABELS: Record<string, string> = { app: "Appen", marketing: "Marknadssajten" };

type DailyRow = { day: string; users: number; sessions: number; visitors: number };

/**
 * Time-series of daily activity: logged-in users (DAU) and unique visitors (by
 * IP). Anonymous sessions stay in the table below, not the chart. Mirrors the Chart.js canvas pattern used in
 * AccuracyTimeline. Expects rows in ascending date order.
 */
const ActivityChart: React.FC<{ rows: DailyRow[] }> = ({ rows }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (!canvasRef.current || rows.length === 0) return;
    if (chartRef.current) chartRef.current.destroy();

    const labels = rows.map((r) =>
      new Date(r.day).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    );

    chartRef.current = new Chart(canvasRef.current, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Inloggade",
            data: rows.map((r) => r.users),
            borderColor: "#2054a2",
            backgroundColor: "rgba(32,84,162,0.12)",
            fill: true,
            tension: 0.3,
            pointRadius: 2,
            pointHoverRadius: 5,
          },
          {
            label: "Unika besökare (IP)",
            data: rows.map((r) => r.visitors),
            borderColor: "#16a34a",
            backgroundColor: "rgba(22,163,74,0.06)",
            fill: false,
            borderDash: [5, 4],
            tension: 0.3,
            pointRadius: 2,
            pointHoverRadius: 5,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: { legend: { display: true, position: "bottom" } },
        scales: {
          x: { ticks: { color: "rgba(0,0,0,0.6)" }, grid: { display: false } },
          y: { beginAtZero: true, ticks: { color: "rgba(0,0,0,0.6)", precision: 0 } },
        },
      },
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [rows]);

  if (rows.length === 0) return <div className="sp-empty">Inga händelser ännu.</div>;
  return (
    <div style={{ height: "300px" }}>
      <canvas ref={canvasRef} />
    </div>
  );
};

/**
 * Merge per-day series from DAU / anon-sessions / unique-IPs into a single
 * descending table keyed by day. Each source supplies one column; missing days
 * fall back to 0.
 */
function mergeDailySeries(
  dau: InsightsTabProps["telemetry"] extends infer T
    ? T extends { dau: infer D }
      ? D
      : never
    : never,
  anon: Array<{ day: string; sessions: number }>,
  ips: Array<{ day: string; visitors: number }> | undefined,
): Array<{ day: string; users: number; sessions: number; visitors: number }> {
  const byDay = new Map<string, { users: number; sessions: number; visitors: number }>();
  const ensure = (d: string) => {
    if (!byDay.has(d)) byDay.set(d, { users: 0, sessions: 0, visitors: 0 });
    return byDay.get(d)!;
  };
  (dau as Array<{ day: string; users: number }>).forEach((r) => {
    ensure(r.day).users = r.users;
  });
  anon.forEach((r) => {
    ensure(r.day).sessions = r.sessions;
  });
  (ips || []).forEach((r) => {
    ensure(r.day).visitors = r.visitors;
  });
  return Array.from(byDay.entries())
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([day, v]) => ({ day, ...v }));
}

/**
 * A small ranked list used inside an sp-card. Bars are rendered as percentage
 * of the top row so it reads like a horizontal bar chart without bringing in a
 * chart library.
 */
const TopList: React.FC<{
  title: string;
  rows: TopRow[];
  empty: string;
  accentClass?: string;
  formatTarget?: (t: string) => React.ReactNode;
  limit?: number;
}> = ({ title, rows, empty, accentClass = "sp-bar--accent", formatTarget, limit = 8 }) => {
  const top = rows.slice(0, limit);
  const max = top.length > 0 ? Math.max(...top.map((r) => r.hits)) : 1;
  return (
    <div className="sp-card insights-toplist">
      <div className="sp-card__head">
        <h3 className="sp-card__title">{title}</h3>
        {rows.length > limit && (
          <span className="sp-card__sub">
            visar {limit} av {rows.length}
          </span>
        )}
      </div>
      {top.length === 0 ? (
        <div className="sp-empty">{empty}</div>
      ) : (
        <ul className="insights-bars">
          {top.map((r) => (
            <li key={r.target} className="insights-bar">
              <div className="insights-bar__label">
                {formatTarget ? formatTarget(r.target) : r.target}
              </div>
              <div className="insights-bar__track">
                <div
                  className={`insights-bar__fill ${accentClass}`}
                  style={{ width: `${Math.max(4, (r.hits / max) * 100)}%` }}
                />
              </div>
              <div className="insights-bar__value">{r.hits.toLocaleString()}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

type ForumStats = NonNullable<NonNullable<InsightsTabProps["telemetry"]>["forum"]>;

// Forum section: totals for the window, where forum visits come from, posts per
// day, and the most viewed threads (view counter is all-time).
// One bar per day across the whole window (missing days count as 0), with the
// value above each bar and date ticks below, so gaps and totals are readable.
const DayBars: React.FC<{
  rows: Array<{ day: string; value: number }>;
  days: number;
  locale: string;
  unit: string;
}> = ({ rows, days, locale, unit }) => {
  const byDay = new Map(rows.map((r) => [r.day, r.value]));
  const series: Array<{ day: string; value: number }> = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
    const key = d.toLocaleDateString("sv-SE");
    series.push({ day: key, value: byDay.get(key) ?? 0 });
  }
  const max = Math.max(1, ...series.map((d) => d.value));
  const total = series.reduce((n, d) => n + d.value, 0);
  const tickEvery = Math.max(1, Math.ceil(series.length / 7));
  // Long windows: thinner gaps and no per-bar numbers (the tooltip has them).
  const dense = series.length > 45;
  const gap = { gap: dense ? 1 : 3 };
  const label = (day: string) =>
    new Date(`${day}T12:00:00`).toLocaleDateString(locale, { day: "numeric", month: "short" });
  return (
    <div className="insights-daybars" aria-label={`${total} ${unit} senaste ${days} dagarna`}>
      <div className="insights-daybars__plot" style={gap}>
        {series.map((d) => (
          <div key={d.day} className="insights-daybars__col" title={`${label(d.day)}: ${d.value} ${unit}`}>
            {d.value > 0 && !dense && <span className="insights-daybars__value">{d.value}</span>}
            <div
              className={`insights-daybars__bar${d.value === 0 ? " is-zero" : ""}`}
              style={{ height: `${(d.value / max) * 100}%` }}
            />
          </div>
        ))}
      </div>
      <div className="insights-daybars__axis" style={gap}>
        {series.map((d, i) => (
          <span key={d.day}>{(i % tickEvery === 0 || i === series.length - 1) ? label(d.day) : ""}</span>
        ))}
      </div>
      <div className="insights-daybars__total">
        {total} {unit} senaste {days} dagarna
      </div>
    </div>
  );
};

const UserInsights: React.FC<{ users: UserStats; days: number; locale: string }> = ({ users, days, locale }) => {
  const t = users.totals;
  const delta = t.last7 - t.prev7;
  const tiles = [
    {
      label: "Nya senaste 7 dagarna",
      value: t.last7,
      sub: delta === 0 ? "samma som veckan innan" : `${delta > 0 ? "+" : ""}${delta} mot veckan innan`,
    },
    { label: `Nya (${days}d)`, value: t.in_window, sub: "i perioden" },
    { label: "Aktiva senaste 7 dagarna", value: t.active7, sub: "inloggade eller aktiva" },
    { label: "Konton totalt", value: t.total, sub: "alla registrerade" },
  ];
  const source = (u: NewUser) => u.utm_source || u.referrer_host || "direkt";
  return (
    <div className="sp-card">
      <div className="sp-card__head">
        <h3 className="sp-card__title">Nya användare</h3>
      </div>
      <div className="insights-forum-tiles">
        {tiles.map((x) => (
          <div key={x.label} className="sp-tile">
            <div className="sp-tile__label">{x.label}</div>
            <div className="sp-tile__value">{x.value.toLocaleString()}</div>
            <div className="sp-tile__sub">{x.sub}</div>
          </div>
        ))}
      </div>
      <h4 className="sp-card__title" style={{ fontSize: "0.95rem", marginTop: 20 }}>
        Registreringar per dag
      </h4>
      <DayBars
        rows={users.signupsPerDay.map((d) => ({ day: d.day, value: d.signups }))}
        days={days}
        locale={locale}
        unit="nya konton"
      />
      <h4 className="sp-card__title" style={{ fontSize: "0.95rem", marginTop: 20 }}>
        Registrerade senaste veckan
      </h4>
      {users.newLastWeek.length === 0 ? (
        <div className="sp-empty">Inga nya konton senaste 7 dagarna.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
        <table className="sp-table insights-users-table">
          <thead>
            <tr>
              <th>Användare</th>
              <th>Plan</th>
              <th>Skapad</th>
              <th>Senast inloggad</th>
              <th>Senast aktiv</th>
              <th>Källa</th>
            </tr>
          </thead>
          <tbody>
            {users.newLastWeek.map((u) => (
              <tr key={u.id}>
                <td>
                  <div>{u.username || u.email.split("@")[0]}</div>
                  <div className="dim">{u.email}</div>
                  {!u.email_verified && <span className="sp-chip">ej verifierad</span>}
                </td>
                <td>{u.plan_name || "—"}</td>
                <td title={formatDateTime(u.created_at, locale)}>{formatRelative(u.created_at, locale)}</td>
                <td title={formatDateTime(u.last_login_at, locale)}>{formatRelative(u.last_login_at, locale)}</td>
                <td title={formatDateTime(u.last_seen_at, locale)}>{formatRelative(u.last_seen_at, locale)}</td>
                <td>
                  {u.country && <>{countryFlag(u.country)} </>}
                  {source(u)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
};

const ForumInsights: React.FC<{ forum: ForumStats; days: number; locale: string }> = ({ forum, days, locale }) => {
  const t = forum.totals;
  const tiles = [
    { label: "Trådvisningar", value: t.views, sub: "totalt" },
    { label: "Nya trådar", value: t.threads, sub: "i perioden" },
    { label: "Inlägg av medlemmar", value: t.posts, sub: `${t.posters} skribenter` },
    { label: "Forumnamn", value: t.named_members, sub: "medlemmar med namn" },
  ];
  return (
    <>
      <div className="sp-card">
        <div className="sp-card__head">
          <h3 className="sp-card__title">Forum</h3>
          <Link className="sp-card__sub" to="/forum">
            Till forumet →
          </Link>
        </div>
        <div className="insights-forum-tiles">
          {tiles.map((x) => (
            <div key={x.label} className="sp-tile">
              <div className="sp-tile__label">{x.label}</div>
              <div className="sp-tile__value">{x.value.toLocaleString()}</div>
              <div className="sp-tile__sub">{x.sub}</div>
            </div>
          ))}
        </div>
        <h4 className="sp-card__title" style={{ fontSize: "0.95rem", marginTop: 20 }}>
          Inlägg per dag
        </h4>
        <DayBars
          rows={forum.postsPerDay.map((d) => ({ day: d.day, value: d.posts }))}
          days={days}
          locale={locale}
          unit="inlägg"
        />
      </div>
      <div className="insights-grid">
        <TopList
          title="Forumbesök per källa"
          rows={forum.pageViews}
          empty="Inga forumbesök ännu."
          accentClass="sp-bar--info"
          formatTarget={(s) => SOURCE_LABELS[s] ?? s}
        />
        <div className="sp-card insights-toplist">
          <div className="sp-card__head">
            <h3 className="sp-card__title">Mest visade trådar</h3>
          </div>
          {forum.topThreads.length === 0 ? (
            <div className="sp-empty">Inga trådar ännu.</div>
          ) : (
            <table className="sp-table">
              <thead>
                <tr>
                  <th>Tråd</th>
                  <th className="num">Visningar</th>
                  <th className="num">Svar</th>
                </tr>
              </thead>
              <tbody>
                {forum.topThreads.map((th) => (
                  <tr key={th.id}>
                    <td>
                      <Link to={`/forum/t/${th.id}`}>{th.title}</Link>
                      {th.kind === "daily" && <span className="sp-chip" style={{ marginLeft: 6 }}>daglig</span>}
                    </td>
                    <td className="num">{th.views.toLocaleString()}</td>
                    <td className="num">{Math.max(0, th.replies)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

export const InsightsTab: React.FC<InsightsTabProps> = ({
  telemetry,
  telemetryLoading,
  canSeeUserInsights,
  days,
  onDaysChange,
}) => {
  const { locale } = useLanguage();

  const windowSelector = (
    <select
      className="sp-select"
      value={days}
      onChange={(e) => onDaysChange(parseInt(e.target.value))}
    >
      <option value={7}>7 dagar</option>
      <option value={30}>30 dagar</option>
      <option value={90}>90 dagar</option>
    </select>
  );

  if (telemetryLoading) {
    return (
      <>
        <div className="insights-header">
          <h2 className="sp-card__title">Användarinsikter</h2>
          {windowSelector}
        </div>
        <div className="sp-card">
          <div className="sp-empty">
            <span className="sp-spinner" />
          </div>
        </div>
      </>
    );
  }
  // Guard against a non-telemetry payload (e.g. a 500 error body that the API
  // layer resolves rather than throws) so a bad response can't crash the page.
  if (!telemetry || !Array.isArray(telemetry.topRoutes)) {
    return (
      <>
        <div className="insights-header">
          <h2 className="sp-card__title">Användarinsikter</h2>
          {windowSelector}
        </div>
        <div className="sp-card">
          <div className="sp-empty">Ingen telemetri kunde laddas.</div>
        </div>
      </>
    );
  }

  const ipsTotal = telemetry.uniqueIpsTotal || { total: 0, authed: 0, anon: 0 };
  const totalPageViews = telemetry.topRoutes.reduce((s, r) => s + r.hits, 0);
  // Page views split by origin. Server defaults legacy (pre-source) events to
  // "app", so anything not explicitly "marketing" belongs in the app bucket.
  const appRoutes = telemetry.topRoutes.filter((r) => r.source !== "marketing");
  const marketingRoutes = telemetry.topRoutes.filter((r) => r.source === "marketing");
  const appPageViews = appRoutes.reduce((s, r) => s + r.hits, 0);
  const marketingPageViews = marketingRoutes.reduce((s, r) => s + r.hits, 0);
  const totalCtaClicks = telemetry.ctaClicks.reduce((s, r) => s + r.hits, 0);
  const totalModalOpens = telemetry.modalOpens.reduce((s, r) => s + r.hits, 0);
  const dailyDesc = mergeDailySeries(telemetry.dau, telemetry.anonVisits, telemetry.uniqueIps);
  const dailyChart = [...dailyDesc].reverse(); // ascending for the time axis
  const dailyTable = dailyDesc.slice(0, 14);

  return (
    <>
      <div className="insights-header">
        <h2 className="sp-card__title">Användarinsikter</h2>
        {windowSelector}
      </div>

      {/* Headline KPI tiles */}
      <div className="insights-kpis">
        <div className="sp-tile">
          <div className="sp-tile__label">Unika besökare ({days}d)</div>
          <div className="sp-tile__value">{ipsTotal.total.toLocaleString()}</div>
          <div className="sp-tile__sub">distinkta IP-adresser</div>
        </div>
        <div className="sp-tile">
          <div className="sp-tile__label">Inloggade</div>
          <div className="sp-tile__value">{ipsTotal.authed.toLocaleString()}</div>
          <div className="sp-tile__sub">unika IP med konto</div>
        </div>
        <div className="sp-tile">
          <div className="sp-tile__label">Anonyma</div>
          <div className="sp-tile__value">{ipsTotal.anon.toLocaleString()}</div>
          <div className="sp-tile__sub">unika IP utan konto</div>
        </div>
        <div className="sp-tile">
          <div className="sp-tile__label">Sidvisningar</div>
          <div className="sp-tile__value">{totalPageViews.toLocaleString()}</div>
          <div className="sp-tile__sub">
            {appPageViews.toLocaleString()} app · {marketingPageViews.toLocaleString()} marknad
          </div>
        </div>
        <div className="sp-tile">
          <div className="sp-tile__label">CTA-klick</div>
          <div className="sp-tile__value">{totalCtaClicks.toLocaleString()}</div>
          <div className="sp-tile__sub">{totalModalOpens.toLocaleString()} modaler</div>
        </div>
      </div>

      {telemetry.users && <UserInsights users={telemetry.users} days={days} locale={locale} />}

      {/* Daily activity chart */}
      <div className="sp-card">
        <div className="sp-card__head">
          <h3 className="sp-card__title">Aktivitet per dag</h3>
          <span className="sp-card__sub">senaste {days} dagar</span>
        </div>
        <ActivityChart rows={dailyChart} />
      </div>

      {/* Daily activity table (exact numbers) */}
      <div className="sp-card">
        <div className="sp-card__head">
          <h3 className="sp-card__title">Aktivitet per dag – tabell</h3>
          <span className="sp-card__sub">senaste 14 dagar</span>
        </div>
        {dailyTable.length === 0 ? (
          <div className="sp-empty">Inga händelser ännu.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="sp-table">
              <thead>
                <tr>
                  <th>Dag</th>
                  <th className="num">Unika besökare (IP)</th>
                  <th className="num">Inloggade</th>
                  <th className="num">Anonyma sessioner</th>
                </tr>
              </thead>
              <tbody>
                {dailyTable.map((r) => (
                  <tr key={r.day}>
                    <td>{r.day}</td>
                    <td className="num">{r.visitors.toLocaleString()}</td>
                    <td className="num">{r.users.toLocaleString()}</td>
                    <td className="num">{r.sessions.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Page views split by origin: app (SPA) vs marketing site */}
      <div className="insights-grid">
        <TopList
          title="Topp-sidor – App"
          rows={appRoutes}
          empty="Inga sidvisningar."
          accentClass="sp-bar--accent"
          formatTarget={(t) => <code style={{ fontSize: "0.8rem" }}>{t}</code>}
        />
        <TopList
          title="Topp-sidor – Marknad"
          rows={marketingRoutes}
          empty="Inga sidvisningar."
          accentClass="sp-bar--info"
          formatTarget={(t) => <code style={{ fontSize: "0.8rem" }}>{t}</code>}
        />
      </div>

      {/* Top content grid: areas, CTAs, modals */}
      <div className="insights-grid">
        <TopList
          title="Områdesval"
          rows={telemetry.topAreas}
          empty="Inga val ännu."
          accentClass="sp-bar--info"
        />
        <TopList
          title="CTA-klick"
          rows={telemetry.ctaClicks}
          empty="Inga klick ännu."
          accentClass="sp-bar--success"
        />
        <TopList
          title="Modaler öppnade"
          rows={telemetry.modalOpens}
          empty="Inga modaler öppnade."
          accentClass="sp-bar--warn"
          formatTarget={featureLabel}
        />
        <TopList
          title="Funktioner"
          rows={telemetry.featureUse || []}
          empty="Inga funktionsklick ännu."
          accentClass="sp-bar--accent"
          formatTarget={featureLabel}
        />
      </div>

      {telemetry.forum && <ForumInsights forum={telemetry.forum} days={days} locale={locale} />}

      {/* Geography + acquisition */}
      <div className="insights-grid">
        <TopList
          title="Länder"
          rows={telemetry.topCountries || []}
          empty="Ingen platsdata ännu."
          accentClass="sp-bar--accent"
          formatTarget={(t) => (
            <span>
              {countryFlag(t)} {countryName(t, locale)}
            </span>
          )}
        />
        <TopList
          title="Regioner (Sverige)"
          rows={telemetry.topRegions || []}
          empty="Ingen regiondata ännu."
          accentClass="sp-bar--info"
          formatTarget={(t) => seRegionName(t)}
        />
        <TopList
          title="Trafikkällor"
          rows={telemetry.topSources || []}
          empty="Ingen källdata ännu."
          accentClass="sp-bar--success"
        />
        <TopList
          title="Prognoslängd (7d / 30d)"
          rows={telemetry.forecastRange || []}
          empty="Inga val ännu."
          accentClass="sp-bar--warn"
        />
      </div>

      {/* Per-user activity (gated) */}
      {canSeeUserInsights && <UserActivityPanel />}

      {/* Free-tier at limit */}
      <div className="sp-card">
        <div className="sp-card__head">
          <h3 className="sp-card__title">Free-tier vid gräns</h3>
          <span className="sp-card__sub">≥ 5 aktiva dagar denna månad</span>
        </div>
        {telemetry.freeTierAtLimit.length === 0 ? (
          <div className="sp-empty">Ingen vid gränsen.</div>
        ) : (
          <table className="sp-table">
            <thead>
              <tr>
                <th>E-post</th>
                <th className="num">Aktiva dagar</th>
              </tr>
            </thead>
            <tbody>
              {telemetry.freeTierAtLimit.map((r) => (
                <tr key={r.account_id}>
                  <td>{r.email}</td>
                  <td className="num">
                    <span className="sp-chip sp-chip--warn">{r.active_days} dagar</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};
